import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, ChevronDown, ChevronUp, Trash2, Loader2, Sparkles } from 'lucide-react';
import type { VoiceSuggestions } from '@/hooks/useVoiceSuggestions';

/**
 * Inline, always-in-the-main-view voice capture strip — not a modal.
 * Collapsed by default to a slim status bar (mic toggle + live status);
 * expands in place to show the rolling transcript. Suggestions themselves
 * are never shown here — they're rendered as highlight indicators directly
 * on the matching ITASC chips (see `controls.tsx`'s `suggested` prop),
 * consistent with keeping fact/inference/advice visually distinct rather
 * than duplicating the same content in two places.
 */
export function VoiceCapturePanel({ voice }: { voice: VoiceSuggestions }) {
  const { transcript, extracting, unavailableReason } = voice;
  const { available, listening, segments, interimText, start, stop, clear, source, error } = transcript;
  const [expanded, setExpanded] = useState(false);
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (expanded) transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight });
  }, [segments, interimText, expanded]);

  const status = !available
    ? 'Voice off'
    : listening
      ? extracting
        ? 'Listening — analysing…'
        : 'Listening'
      : 'Not listening';

  return (
    <div className="rounded-md border border-border bg-surface-2">
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        <button
          type="button"
          onClick={listening ? stop : start}
          disabled={!available}
          aria-label={listening ? 'Stop voice capture' : 'Start voice capture'}
          className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
            listening ? 'border-red-500/40 bg-red-500/10 text-red-500' : 'border-border bg-surface text-muted hover:text-accent'
          }`}
        >
          {listening ? <MicOff size={12} /> : <Mic size={12} />}
        </button>

        <span className="flex items-center gap-1 text-[11px] text-muted">
          {extracting && <Loader2 size={11} className="animate-spin" />}
          {listening && !extracting && <Sparkles size={11} className="text-amber-400" aria-hidden />}
          {status}
          {listening && (
            <span className="text-muted/60">· {source === 'whisper' ? 'Whisper' : 'on-device'}</span>
          )}
        </span>

        <div className="flex-1" />

        {segments.length > 0 && (
          <button
            type="button"
            onClick={clear}
            className="rounded-md border border-border bg-surface px-1.5 py-1 text-muted hover:text-accent"
            title="Clear transcript"
          >
            <Trash2 size={11} />
          </button>
        )}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="rounded-md border border-border bg-surface px-1.5 py-1 text-muted hover:text-accent"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse transcript' : 'Expand transcript'}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border px-2.5 py-2">
          {!available && (
            <p className="text-[11px] text-muted">
              Needs the Voice setting enabled, and either server-side transcription configured or a browser that
              supports on-device speech recognition (Chrome/Edge). Turn on Voice in the settings menu to use this.
            </p>
          )}
          {error && <p className="text-[11px] text-crit">{error}</p>}
          {available && (
            <>
              <div ref={transcriptRef} className="max-h-32 overflow-y-auto text-[11px] leading-relaxed text-text">
                {segments.length === 0 && !interimText && (
                  <p className="text-muted">Transcript will appear here once you start listening.</p>
                )}
                {segments.map((s) => (
                  <p key={s.id} className="mb-0.5">
                    {s.text}
                  </p>
                ))}
                {interimText && <p className="italic text-muted">{interimText}…</p>}
              </div>
              {unavailableReason && <p className="mt-1.5 text-[10px] text-muted">{unavailableReason}</p>}
              <p className="mt-1.5 text-[10px] text-muted">
                Matching ITASC tags are highlighted <Sparkles size={9} className="inline text-amber-400" aria-hidden />{' '}
                as suggestions appear — hover a highlighted tag to see why, click it to accept.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
